import React from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '../../../contexts/DirectionContext';
import { getUserFriendlyError } from '../../../utils/errorMapping';

interface ErrorDisplayProps {
  /** Error object or message */
  error?: Error | string | null;
  /** Handler for retry/refresh action */
  onRetry: () => void;
  /** Whether to show in compact mode (for in-content errors) */
  compact?: boolean;
  /** Additional class names */
  className?: string;
  /** Whether to show the error icon */
  showIcon?: boolean;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onRetry,
  compact = false,
  className = '',
  showIcon = true
}) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();

  const containerClasses = `
    flex flex-col items-center justify-center
    bg-background-primary transition-colors duration-200
    ${compact ? 'py-8' : 'min-h-[200px]'}
    ${className}
  `;

  const errorMessage = getUserFriendlyError(error, t);

  return (
    <div className={containerClasses}>
      {showIcon && (
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-status-error/10 text-status-error">
          <Icon icon="ph:warning-circle-thin" className="w-8 h-8" />
        </div>
      )}
      
      <h2 className="mt-3 text-lg font-medium text-text-primary transition-colors duration-200">
        {errorMessage}
      </h2>
      
      <span
        onClick={onRetry}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onRetry();
          }
        }}
        role="button"
        tabIndex={0}
        className={`
          mt-2
          text-sm text-primary-500
          cursor-pointer
          transition-colors duration-200
          hover:text-primary-600 active:text-primary-700
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
          ${isRTL ? 'rtl' : 'ltr'}
        `}
      >
        {t('common.retry')}
      </span>
    </div>
  );
}; 